import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-more-analytics',
  templateUrl: './more-analytics.component.html',
  styleUrls: ['./more-analytics.component.scss']
})
export class MoreAnalyticsComponent implements OnInit {
  public insights: any = null;
  public isLoading = false;
  public days = 30;
  public overviewCards: Array<any> = [];
  public healthCards: Array<any> = [];
  public experienceCards: Array<any> = [];
  public painPointCards: Array<any> = [];

  constructor(
    private authenticationService: AuthenticationService,
    private toastr: ToastrService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    const role = this.getCurrentRole();
    const hasAdminSession = role === 'admin' || !!(this.getStoredSession('admin') && this.getStoredSession('admin').token);

    if (!hasAdminSession) {
      this.toastr.error('Admin access is required.', 'Access Denied');
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loadInsights();
  }

  private getCurrentRole(): string {
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      return storedRole.toLowerCase();
    }

    const adminSession = this.getStoredSession('admin');
    if (adminSession && adminSession.token) {
      return 'admin';
    }

    const userSession = this.getStoredSession('user');
    if (userSession && userSession.token) {
      return 'user';
    }

    return '';
  }

  private getStoredSession(key: string): any {
    const rawValue = localStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return null;
    }
  }

  public loadInsights(): void {
    this.isLoading = true;
    this.authenticationService.getPlatformInsights(this.days).subscribe(
      (res: any) => {
        this.insights = res && res.data ? res.data : res;
        this.buildCards();
        this.isLoading = false;
      },
      (error: any) => {
        this.isLoading = false;
        const message = error?.error?.message || 'Unable to load platform insights';
        this.toastr.error(message, 'Analytics Error');
      }
    );
  }

  public onDaysChange(): void {
    this.loadInsights();
  }

  private buildCards(): void {
    const data = this.insights || {};
    const summary = data.summary || {};
    const experienceSignals = data.experienceSignals || {};
    const painPoints = Array.isArray(data.painPoints) ? data.painPoints : [];

    this.overviewCards = [
      { title: 'Total Users', value: this.formatNumber(summary.totalUsers), subtitle: 'All registered users' },
      { title: 'Active Users', value: this.formatNumber(summary.activeUsers), subtitle: 'Users active in the selected window' },
      { title: 'Recent Signups', value: this.formatNumber(summary.recentlyRegisteredUsers), subtitle: 'New users in the last period' },
      { title: 'Verified Profiles', value: this.formatNumber(summary.verifiedProfiles), subtitle: 'Profiles with verification' },
    ];

    this.healthCards = [
      { title: 'Total Likes', value: this.formatNumber(summary.totalLikes), subtitle: 'All likes generated' },
      { title: 'Total Matches', value: this.formatNumber(summary.totalMatches), subtitle: 'Successful pairings' },
      { title: 'Match Coverage', value: this.formatPercent(summary.matchCoverage), subtitle: 'Matches compared with likes' },
      { title: 'Like Success Rate', value: this.formatPercent(summary.likeSuccessRate), subtitle: 'Matches / likes ratio' },
    ];

    this.experienceCards = [
      { title: 'Avg Likes / User', value: this.formatNumber(experienceSignals.averageLikesPerUser), subtitle: 'Engagement intensity' },
      { title: 'Avg Matches / User', value: this.formatNumber(experienceSignals.averageMatchesPerUser), subtitle: 'Matching frequency' },
      { title: 'Chat Adoption Rate', value: this.formatPercent(experienceSignals.chatAdoptionRate), subtitle: 'Matched users who chat' },
    ];

    this.painPointCards = painPoints.map((item: any) => ({
      title: item.label || 'Pain point',
      value: this.formatNumber(item.value),
      subtitle: item.percentage ? `${item.percentage}% of users` : 'Needs attention',
    }));
  }

  private formatNumber(value: number | null | undefined): string {
    return Number(value || 0).toLocaleString('en-US');
  }

  private formatPercent(value: number | null | undefined): string {
    return `${Number(value || 0).toFixed(1)}%`;
  }
}
